import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import ExerciseCard from './ExerciseCard';

const SupersetGroup = ({ 
  supersetId, 
  exercises, 
  onUpdate, 
  onExerciseUpdate, 
  onExerciseRemove,
  phaseOptions 
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [supersetName, setSupersetName] = useState(supersetId);

  const supersetColors = [
    'bg-secondary/10 border-secondary/20 text-secondary',
    'bg-primary/10 border-primary/20 text-primary',
    'bg-warning/10 border-warning/20 text-warning',
    'bg-success/10 border-success/20 text-success'
  ];

  const getColorClass = () => {
    const hash = supersetId?.split('')?.reduce((a, b) => {
      a = ((a << 5) - a) + b?.charCodeAt(0);
      return a & a;
    }, 0);
    return supersetColors?.[Math.abs(hash) % supersetColors?.length];
  };

  const handleRemoveSuperset = () => {
    exercises?.forEach(exercise => {
      const updatedExercise = {
        ...exercise,
        planConfig: {
          ...exercise?.planConfig,
          supersetGroup: null,
          supersetOrder: null
        }
      };
      onExerciseUpdate(exercise?.id, updatedExercise);
    });
  };

  const handleAddExercise = () => {
    // This would typically open a modal or dropdown to select exercises
    console.log('Add exercise to superset:', supersetId);
  };

  const getExecutionPattern = () => {
    const sortedExercises = [...exercises]?.sort((a, b) => 
      (a?.planConfig?.supersetOrder || 0) - (b?.planConfig?.supersetOrder || 0)
    );
    
    return sortedExercises?.map((exercise, index) => {
      const letter = String.fromCharCode(65 + Math.floor(index / 3)); // A, B, C...
      const number = (index % 3) + 1; // 1, 2, 3
      return `${letter}${number}`;
    })?.join(' → ');
  };

  const getTotalSets = () => {
    return Math.max(...exercises?.map(ex => {
      const phase = ex?.phases?.[ex?.planConfig?.selectedPhase];
      return Math.ceil((phase?.minReps + phase?.maxReps) / 2 / 4); // Estimate sets
    }));
  };

  return (
    <div className={`border-2 rounded-lg ${getColorClass()}`}>
      {/* Superset Header */}
      <div className="p-4 border-b border-current/20">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <Icon name="Link" size={20} />
              {isEditing ? (
                <input
                  type="text"
                  value={supersetName}
                  onChange={(e) => setSupersetName(e?.target?.value)}
                  onBlur={() => setIsEditing(false)}
                  onKeyPress={(e) => e?.key === 'Enter' && setIsEditing(false)}
                  className="bg-transparent border-b border-current/50 focus:outline-none focus:border-current"
                  autoFocus
                />
              ) : (
                <h3 
                  className="text-lg font-semibold cursor-pointer hover:underline"
                  onClick={() => setIsEditing(true)}
                >
                  {supersetName}
                </h3>
              )}
            </div>
            
            <div className="flex items-center space-x-4 text-sm opacity-75">
              <span>{exercises?.length} exercises</span>
              <span>•</span>
              <span>{getTotalSets()} sets</span>
              <span>•</span>
              <span>Pattern: {getExecutionPattern()}</span>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleAddExercise}
              iconName="Plus"
              className="breathing-focus opacity-75 hover:opacity-100"
            >
              Add Exercise
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsExpanded(!isExpanded)}
              className="breathing-focus opacity-75 hover:opacity-100"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              <Icon name={isExpanded ? 'ChevronUp' : 'ChevronDown'} size={16} />
            </Button>
            
            <Button
              variant="ghost"
              size="icon"
              onClick={handleRemoveSuperset}
              className="breathing-focus opacity-75 hover:opacity-100 text-error hover:text-error"
              aria-label="Remove superset"
            >
              <Icon name="Unlink" size={16} />
            </Button>
          </div>
        </div>

        {/* Execution Flow Visualization */}
        {isExpanded && (
          <div className="mt-4 p-3 bg-current/5 rounded-lg">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Execution Flow:</span>
              <span className="text-xs opacity-75">3 sets each</span>
            </div>
            
            <div className="flex items-center space-x-2 text-sm">
              {exercises?.sort((a, b) => (a?.planConfig?.supersetOrder || 0) - (b?.planConfig?.supersetOrder || 0))?.map((exercise, index) => (
                  <React.Fragment key={exercise?.id}>
                    {index > 0 && (
                      <Icon name="ArrowRight" size={14} className="opacity-50" />
                    )}
                    <div className="flex items-center space-x-1 px-2 py-1 bg-current/10 rounded">
                      <span className="font-mono font-semibold">
                        {String.fromCharCode(65 + Math.floor(index / 3))}{((index % 3) + 1)}
                      </span>
                      <span className="truncate max-w-24">{exercise?.name}</span>
                    </div>
                  </React.Fragment>
                ))
              }
            </div>
          </div>
        )}
      </div>
      {/* Superset Exercises */}
      {isExpanded && (
        <div className="p-4 space-y-4">
          {exercises?.sort((a, b) => (a?.planConfig?.supersetOrder || 0) - (b?.planConfig?.supersetOrder || 0))?.map((exercise, index) => (
              <div key={exercise?.id} className="relative">
                {/* Exercise Order Indicator */}
                <div className="absolute -left-2 top-4 w-8 h-8 bg-current/20 rounded-full flex items-center justify-center text-sm font-bold z-10">
                  {String.fromCharCode(65 + Math.floor(index / 3))}{((index % 3) + 1)}
                </div>
                
                <div className="ml-6">
                  <ExerciseCard
                    exercise={exercise}
                    index={index}
                    onUpdate={onExerciseUpdate}
                    onRemove={onExerciseRemove}
                    onMove={() => {}}
                    onSupersetCreate={() => {}}
                    phaseOptions={phaseOptions}
                    isInSuperset={true}
                  />
                </div>
              </div>
            ))}
        </div>
      )}
      {/* Superset Footer */}
      <div className="px-4 py-3 border-t border-current/20 bg-current/5">
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center space-x-4">
            <span>Rest between exercises: 30-60 seconds</span>
            <span>•</span>
            <span>Rest between sets: 2-3 minutes</span>
          </div>
          
          <div className="flex items-center space-x-2 opacity-75">
            <Icon name="Clock" size={14} />
            <span>~{exercises?.length * 2.5} min per set</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupersetGroup;